"use client";

/**
 * App-wide host for the Convert-to-Pro modal shown to a user who is ALREADY
 * inside the $1 trial.
 *
 * The mirror of <TrialDismissFallback/>, which sells the trial to a free user.
 * This one exists because of the reported bug: every upgrade CTA in the product
 * routed a trialing user to the credit/plans paywall, which is 403'd for the
 * whole trial (lib/trial/purchase-guard.ts) — a buy screen that cannot buy. The
 * router now dispatches TRIAL_CONVERT_EVENT for that population instead
 * (lib/trial/upgrade-entry-client.ts) and this listens for it.
 *
 * An event + one root-mounted listener, rather than teaching ~22 call sites to
 * mount a modal: most of them live in a 26k-line page, and the existing
 * `if (decision.handled) return;` at each of them is already the correct
 * behaviour once something else opens the surface.
 *
 * The modal itself is TrialExhaustedModal, reused with proactive copy exactly
 * as Settings → Billing already reuses it (components/settings/billing/
 * current-plans-card.tsx). That is deliberate: Settings → Billing is the ONE
 * entry point QA found to be correct, so matching it is what makes the
 * experience consistent everywhere rather than merely different-but-new.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { TrialExhaustedModal } from "@/components/trial/trial-exhausted-modal";
import {
  setModalOpen,
  MODAL_IDS,
  isTrialConvertOpen,
} from "@/lib/ui/modal-coordinator";
import { trialCopy } from "@/lib/trial/config";
import { trialConversionCompleted } from "@/components/trial/conversion-completed";
import {
  TRIAL_CONVERT_EVENT,
  type TrialConvertDetail,
} from "@/lib/trial/upgrade-entry-client";

export function TrialConvertHost() {
  const [open, setOpen] = useState(false);
  const [buildLabel, setBuildLabel] = useState<string | null>(null);
  const [creditsRemaining, setCreditsRemaining] = useState<number | null>(null);
  const [projectId, setProjectId] = useState<string | null>(null);
  // Where the entry point wants this buyer to land afterwards. Null means it
  // did not say, and the modal resolves the page they came from itself.
  const [returnTo, setReturnTo] = useState<string | null>(null);

  // Read inside a listener registered once, so a ref rather than state.
  const openRef = useRef(false);

  useEffect(() => {
    function onConvert(e: Event) {
      const detail = (e as CustomEvent<TrialConvertDetail>).detail ?? {};

      // Already up → ignore. Dispatch comes from ~22 entry points that cannot
      // see each other, so this is where duplicate opens are resolved — the
      // same rule TrialDismissFallback applies to its own event.
      if (openRef.current) return;

      // The project page and Settings → Billing mount their OWN copies of this
      // same modal. If either is up, stand down rather than stack a second,
      // identical modal over it. Asked through isTrialConvertOpen so a mount
      // added later is covered without touching this line. (The reverse
      // direction is guarded at the gate — see app/project/[id]/page.tsx.)
      //
      // Deliberately NOT the dismissed-once latch TrialDismissFallback keeps:
      // this modal only ever opens from a deliberate click, and a click is the
      // user asking again — latching would make the button dead on the second
      // press, which is the failure that latch already had to be narrowed for.
      if (isTrialConvertOpen()) return;

      setBuildLabel(detail.buildLabel ?? null);
      // Kept for the post-conversion confirmation, whose analytics carry the
      // build the user was blocked on. Absent on non-project surfaces → null.
      setProjectId(detail.projectId ?? null);
      setReturnTo(detail.returnTo ?? null);
      // Only a real number is worth passing: the modal splits "not enough trial
      // credits" from "trial credits used up" on it (lib/trial/exhausted-copy),
      // and this is a proactive click, so an absent value must stay null rather
      // than become a 0 that would falsely read as an exhausted pool.
      setCreditsRemaining(
        typeof detail.creditsRemaining === "number"
          ? detail.creditsRemaining
          : null,
      );
      setOpen(true);
    }
    window.addEventListener(TRIAL_CONVERT_EVENT, onConvert);
    return () => window.removeEventListener(TRIAL_CONVERT_EVENT, onConvert);
  }, []);

  // Report open state to the global coordinator so the rated/credit paywalls
  // stand down while this is up instead of stacking over it — the same rule the
  // $1 offer follows. This modal owns its own `open` state, so nothing else can
  // see it without this.
  useEffect(() => {
    openRef.current = open;
    setModalOpen(MODAL_IDS.trialConvert, open);
    return () => setModalOpen(MODAL_IDS.trialConvert, false);
  }, [open]);

  const handleConverted = useCallback(() => {
    setOpen(false);
    // Entitlement sync + the "Welcome to Pro" confirmation, shared with the
    // other two mounts of this modal (the builder page and Settings → Billing)
    // so they cannot diverge. They already had, twice: first the sync (a
    // converted user kept seeing "Trial active · 100/100" until a hard
    // refresh), then the confirmation (only the builder page showed it, so
    // every out-of-credits CTA — which all route HERE — paid $29 and watched
    // the modal just close).
    trialConversionCompleted({ projectId });
  }, [projectId]);

  return (
    <TrialExhaustedModal
      open={open}
      onClose={() => setOpen(false)}
      pill="Convert to Pro"
      dismissLabel="Not now"
      headline={
        buildLabel
          ? `Want more credits? Convert to Pro and finish ${buildLabel}`
          : "Want more credits? Convert to Pro now"
      }
      subheading={`Convert your trial to Pro today for ${trialCopy.convertPriceLabel}/mo and get ${trialCopy.convertCredits} credits right away — buying and plan changes unlock the moment you convert.`}
      creditsRemaining={creditsRemaining}
      returnTo={returnTo}
      onConverted={handleConverted}
    />
  );
}
